import React, { useState, useEffect } from 'react';
import { formatDate } from '../../utils/dateUtils';
import {
  X,
  Clock,
  RotateCcw,
  AlertCircle,
  CheckCircle,
  Calendar,
  DollarSign,
  ShoppingBag
} from 'lucide-react';
import apiService from '../../services/api';
import BaseModal from '../common/BaseModal';
import { useConfirmation } from '../../hooks/useConfirmation';

const ReceiptVersionHistoryModal = ({ isOpen, onClose, receiptId, onVersionRestored }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const { confirm, ConfirmationModal } = useConfirmation();

  useEffect(() => {
    if (isOpen && receiptId) {
      loadVersions();
    }
  }, [isOpen, receiptId]);

  const loadVersions = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getReceiptVersions(receiptId);
      setVersions(data.versions || []);
    } catch (err) {
      console.error('Error loading versions:', err);
      setError('Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (versionNumber) => {
    const confirmed = await confirm(
      'Restore Version',
      `Are you sure you want to restore to version ${versionNumber}? This will create a new version with the current state before restoring.`,
      'warning'
    );

    if (!confirmed) return;

    setRestoring(versionNumber);
    setError(null);
    setSuccess(null);

    try {
      const result = await apiService.restoreReceiptVersion(receiptId, versionNumber);
      setSuccess(`Successfully restored to version ${versionNumber}`);
      await loadVersions(); // Reload versions to show the new one created

      // Notify parent to refresh the receipt
      if (onVersionRestored) {
        onVersionRestored();
      }
    } catch (err) {
      console.error('Error restoring version:', err);
      setError(`Failed to restore version: ${err.message}`);
    } finally {
      setRestoring(null);
    }
  };

  // formatDate imported from utils/dateUtils
  const formatDateTimeLocal = (dateString) => {
    if (!dateString) return 'N/A';
    // For timestamps, parse properly to avoid timezone issues
    const parts = dateString.split('T')[0].split('-');
    const date = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateTotal = (items) => {
    if (!items || !Array.isArray(items)) return 0;
    return items.reduce((sum, item) => sum + (parseFloat(item.total_price) || 0), 0);
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      showCloseButton={false}
      closeOnBackdrop={!restoring}
    >
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Clock className="h-5 w-5 text-indigo-600 dark:text-indigo-500" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Receipt Version History</h3>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={restoring !== null}
          className="text-gray-400 dark:text-gray-500 hover:text-gray-500 dark:hover:text-gray-400 disabled:opacity-50"
          aria-label="Close modal"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Body */}
      <div className="p-6 space-y-3">
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-400 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
            </div>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md p-3">
            <div className="flex items-start gap-2">
              <CheckCircle className="h-5 w-5 text-green-400 dark:text-green-400 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-green-700 dark:text-green-400">{success}</span>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 dark:border-indigo-500"></div>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">Loading version history...</p>
          </div>
        ) : versions.length === 0 ? (
          /* Empty State */
          <div className="text-center py-12">
            <Clock className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
            <p className="text-sm text-gray-500 dark:text-gray-400">No version history available</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Versions will be created when you edit this receipt</p>
          </div>
        ) : (
          /* Versions List */
          <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
            {versions.map((version) => (
              <div
                key={version.id}
                className="rounded-lg p-3 bg-white dark:bg-gray-800 transition-colors"
                style={{ border: '2px solid #d1d5db' }}
              >
                {/* Version Header */}
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-200">
                        v{version.version_number}
                      </span>
                      {version.change_reason && (
                        <span className="text-xs text-gray-500 dark:text-gray-400 italic truncate">
                          {version.change_reason}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {formatDate(version.created_at)}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRestore(version.version_number)}
                    disabled={restoring !== null}
                    className="ml-3 flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-indigo-600 dark:bg-indigo-700 text-white rounded-md hover:bg-indigo-700 dark:hover:bg-indigo-600 disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
                  >
                    {restoring === version.version_number ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        <span>Restoring...</span>
                      </>
                    ) : (
                      <>
                        <RotateCcw className="h-3 w-3" />
                        <span>Restore</span>
                      </>
                    )}
                  </button>
                </div>

                {/* Version Details */}
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
                    <ShoppingBag className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                    <span className="text-gray-500 dark:text-gray-400">Merchant:</span>
                    <span className="font-medium truncate">{version.merchant || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
                    <Calendar className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                    <span className="text-gray-500 dark:text-gray-400">Date:</span>
                    <span className="font-medium">
                      {version.receipt_date
                        ? formatDate(version.receipt_date)
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
                    <DollarSign className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                    <span className="text-gray-500 dark:text-gray-400">Total:</span>
                    <span className="font-medium">${version.total_amount?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-gray-700 dark:text-gray-300">
                    <ShoppingBag className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                    <span className="text-gray-500 dark:text-gray-400">Items:</span>
                    <span className="font-medium">{version.items?.length || 0}</span>
                  </div>
                </div>

                {/* Items List */}
                {version.items && version.items.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Items:</div>
                    <div className="bg-gray-50 dark:bg-gray-900 rounded-md p-2 max-h-32 overflow-y-auto">
                      <div className="space-y-1">
                        {version.items.map((item, idx) => (
                          <div key={idx} className="text-xs flex items-start justify-between gap-2">
                            <span className="text-gray-700 dark:text-gray-300 flex-1 min-w-0">
                              {item.category_icon && <span className="mr-1">{item.category_icon}</span>}
                              <span className="truncate">{item.description || 'No description'}</span>
                              {item.quantity > 1 && (
                                <span className="text-gray-500 dark:text-gray-400 ml-1">×{item.quantity}</span>
                              )}
                            </span>
                            <span className="text-gray-900 dark:text-white font-medium flex-shrink-0">
                              ${(item.total_price || 0).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
        <button
          onClick={onClose}
          disabled={restoring !== null}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
        >
          Close
        </button>
      </div>

      <ConfirmationModal />
    </BaseModal>
  );
};

export default ReceiptVersionHistoryModal;
